import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Button from '../../../../../components/ui/Button/Button';
import Switch from '../../../../../components/ui/Switch/Switch';
import {
  DirectMenuState, getDirectMenuState, setDirectMenuState,
  ImplementedRolloutFeature, getFeatureState, setFeatureState,
} from '../../../../../crud/management_api/feature_rollout';
import { directMenuMessages as messages } from '../../../../../configuration/directMenuRollout';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: DirectMenuState };

interface Props {
  localId: number;
  onSaved: () => void;
  feature?: ImplementedRolloutFeature;
  copy?: { [Key in keyof typeof messages]: string };
}

/** Isolated superadmin opt-in control; never mounts for ordinary restaurants. */
export default function DirectMenuSetting({
  localId, onSaved, feature = 'direct_menu', copy = messages,
}: Props) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    setState({ status: 'loading' });
    setSaving(false);
    const read = feature === 'direct_menu'
      ? getDirectMenuState(localId, controller.signal)
      : getFeatureState(localId, feature, controller.signal);
    read.then(data => {
      if (!controller.signal.aborted) setState({ status: 'ready', data });
    }).catch(() => {
      if (!controller.signal.aborted) {
        setState({ status: 'error', message: copy.unavailable });
      }
    });
    return () => {
      controller.abort();
      request.current?.abort();
    };
  }, [localId, reload, feature, copy.unavailable]);

  const save = async (enabled: boolean) => {
    if (state.status !== 'ready' || saving) return;
    const controller = new AbortController();
    request.current = controller;
    setSaving(true);
    try {
      const data = feature === 'direct_menu'
        ? await setDirectMenuState(localId, enabled, state.data.revision, controller.signal)
        : await setFeatureState(localId, feature, enabled, state.data.revision, controller.signal);
      if (!controller.signal.aborted) {
        setState({ status: 'ready', data });
        toast.success(copy.saved);
        onSaved();
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        const conflict = axios.isAxiosError(error) && error.response?.status === 409;
        setState({ status: 'error', message: conflict ? copy.conflict : copy.unavailable });
      }
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  return (
    <section aria-label={copy.title}>
      <h3>{copy.title}</h3>
      <p>{copy.description}</p>
      {state.status === 'loading' && <p role="status">{copy.loading}</p>}
      {state.status === 'error' && <>
        <p role="alert">{state.message}</p>
        <Button variant="secondary" onClick={() => setReload(value => value + 1)}>
          {copy.retry}
        </Button>
      </>}
      {state.status === 'ready' && <>
        {state.data.revision === 0 && <p>{copy.empty}</p>}
        <Switch
          id={`feature-rollout-${feature}`}
          label={copy.enable}
          checked={state.data.enabled}
          onChange={save}
          disabled={saving}
        />
        {saving && <p role="status">{copy.saving}</p>}
      </>}
    </section>
  );
}
